import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Zap, Settings, Cpu, Wrench, LayoutDashboard, FileText, Share2, Info } from 'lucide-react';
import { PanelSection } from './PanelSection';
import { EquipmentSection } from './EquipmentSection';
import { MaintenanceSection } from './MaintenanceSection';
import { CsiItemSubmittalsSection } from '../shared/CsiItemSubmittalsSection';
import { DeviceSection } from './DeviceSection';
import { ScheduleSection } from './ScheduleSection';
import { GeneralInfoSection } from './GeneralInfoSection';
import { ShareSection } from '../shared/ShareSection';
import { ConfirmDialog } from '../../components/ConfirmDialog';

const TABS = [
  { value: 'elec-general', label: 'General Info', icon: Info, noCount: true },
  { value: 'elec-equipment', label: 'Equipments', icon: Settings },
  { value: 'elec-devices', label: 'Devices', icon: Cpu },
  { value: 'elec-panel', label: 'Panels', icon: Zap },
  { value: 'elec-schedules', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'elec-maintenance', label: 'Maintenances', icon: Wrench },
  { value: 'elec-submittals', label: 'Submittals', icon: FileText },
];

export const ElectricalSystemsSection = ({
  activeSubTab, onSubTabChange,
  projectId, csiItem, canEditCSIItems, canManageProject,
  shareCompanySearch, showCompanySuggestions, companySuggestions,
  highlightedIndex, searchingCompanies,
  handleSearchCompanies, handleCompanySearchKeyDown,
  setShowCompanySuggestions, setHighlightedIndex,
  handleSelectCompany, handleUnshareCSI,
  project, user,
}) => {
  // Config-driven tab counts: keyed by tab value, e.g. { 'elec-panel': 3 }
  const [tabCounts, setTabCounts] = useState({});
  const [generalInfoDirty, setGeneralInfoDirty] = useState(false);
  const [confirmState, setConfirmState] = useState({ open: false });
  const pendingTabRef = useRef(null);

  // Fetch all counts on mount (single lightweight API call)
  useEffect(() => {
    if (!projectId) return;
    const fetchCounts = async () => {
      try {
        const res = await axios.get(`${API}/electrical-systems/${projectId}/counts`);
        setTabCounts(res.data);
      } catch { /* tab counts are best-effort */ }
    };
    fetchCounts();
  }, [projectId]);

  // Child sections call this after create/delete to update their own count
  const handleTabCount = useCallback((tabValue) => (count) => {
    setTabCounts(prev => {
      if (prev[tabValue] === count) return prev;
      return { ...prev, [tabValue]: count };
    });
  }, []);

  const currentTab = activeSubTab && (TABS.some(t => t.value === activeSubTab) || activeSubTab === 'elec-share') ? activeSubTab : TABS[0].value;

  // Intercept tab change when general info has unsaved changes
  const handleTabChange = useCallback((newTab) => {
    if (generalInfoDirty && currentTab === 'elec-general' && newTab !== 'elec-general') {
      pendingTabRef.current = newTab;
      setConfirmState({
        open: true,
        title: 'Unsaved Changes',
        description: 'You have unsaved changes. If you leave now, your changes will be lost.',
        confirmText: 'Leave',
        cancelText: 'Stay',
        variant: 'destructive',
        onConfirm: () => {
          setConfirmState({ open: false });
          setGeneralInfoDirty(false);
          onSubTabChange(pendingTabRef.current);
          pendingTabRef.current = null;
        },
      });
      return;
    }
    onSubTabChange(newTab);
  }, [generalInfoDirty, currentTab, onSubTabChange]);

  const getTabLabel = (tab) => {
    if (tab.noCount) return tab.label;
    const count = tabCounts[tab.value] ?? 0;
    return `${tab.label} (${count})`;
  };

  return (
    <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-6">
      <TabsList className="flex items-center w-full gap-2" data-testid="electrical-tabs-list">
        <div className="flex-1 grid grid-cols-7 gap-0">
          {TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              data-testid={`tab-${tab.value}`}
              className="flex items-center gap-1.5 text-xs sm:text-sm"
            >
              <tab.icon className="h-3.5 w-3.5 hidden sm:block" />
              {getTabLabel(tab)}
            </TabsTrigger>
          ))}
        </div>
        {canManageProject && (
          <TabsTrigger value="elec-share" data-testid="tab-elec-share" className="flex-shrink-0">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </TabsTrigger>
        )}
      </TabsList>

      {/* Sharing Info - below tabs, matching Division 8 pattern */}
      {canManageProject && (
        <div className="text-xs text-slate-600 -mt-2" data-testid="elec-share-info">
          Shared with: {csiItem?.assignedCompanyName ? (
            <span className="font-medium text-slate-900">{csiItem.assignedCompanyName}</span>
          ) : (
            <span className="text-slate-400">N/A</span>
          )}
        </div>
      )}

      {/* General Info Tab */}
      <TabsContent value="elec-general" data-testid="content-elec-general">
        <GeneralInfoSection
          projectId={projectId}
          canEditCSIItems={canEditCSIItems}
          onDirtyChange={setGeneralInfoDirty}
        />
      </TabsContent>

      {/* Panel Tab - Active */}
      <TabsContent value="elec-panel" data-testid="content-elec-panel">
        <PanelSection
          projectId={projectId}
          csiItem={csiItem}
          canEditCSIItems={canEditCSIItems}
          canManageProject={canManageProject}
          onItemCount={handleTabCount('elec-panel')}
        />
      </TabsContent>

      {/* Equipment Tab - Active */}
      <TabsContent value="elec-equipment" data-testid="content-elec-equipment">
        <EquipmentSection
          projectId={projectId}
          csiItem={csiItem}
          canEditCSIItems={canEditCSIItems}
          canManageProject={canManageProject}
          onItemCount={handleTabCount('elec-equipment')}
        />
      </TabsContent>

      {/* Maintenance Tab - Active */}
      <TabsContent value="elec-maintenance" data-testid="content-elec-maintenance">
        <MaintenanceSection
          projectId={projectId}
          csiItem={csiItem}
          canEditCSIItems={canEditCSIItems}
          canManageProject={canManageProject}
          onItemCount={handleTabCount('elec-maintenance')}
        />
      </TabsContent>

      {/* Share Tab - matching Division 8 pattern */}
      {csiItem && (
        <TabsContent value="elec-share" data-testid="content-elec-share">
          <ShareSection
            asContent
            tabValue=""
            inputIdPrefix="elec-share"
            csiItem={csiItem}
            project={project}
            user={user}
            shareCompanySearch={shareCompanySearch}
            showCompanySuggestions={showCompanySuggestions}
            companySuggestions={companySuggestions}
            highlightedIndex={highlightedIndex}
            searchingCompanies={searchingCompanies}
            handleSearchCompanies={handleSearchCompanies}
            handleCompanySearchKeyDown={handleCompanySearchKeyDown}
            setShowCompanySuggestions={setShowCompanySuggestions}
            setHighlightedIndex={setHighlightedIndex}
            handleSelectCompany={handleSelectCompany}
            handleUnshareCSI={handleUnshareCSI}
          />
        </TabsContent>
      )}

      {/* Submittals Tab - Active */}
      <TabsContent value="elec-submittals" data-testid="content-elec-submittals">
        <CsiItemSubmittalsSection
          projectId={projectId}
          csiItem={csiItem}
          canEditCSIItems={canEditCSIItems}
          canManageProject={canManageProject}
          onItemCount={handleTabCount('elec-submittals')}
        />
      </TabsContent>

      {/* Devices Tab - Active */}
      <TabsContent value="elec-devices" data-testid="content-elec-devices">
        <DeviceSection
          projectId={projectId}
          csiItem={csiItem}
          canEditCSIItems={canEditCSIItems}
          canManageProject={canManageProject}
          onItemCount={handleTabCount('elec-devices')}
        />
      </TabsContent>

      {/* Schedule Tab - Read-only Location Aggregation View */}
      <TabsContent value="elec-schedules" data-testid="content-elec-schedules">
        <ScheduleSection
          projectId={projectId}
          csiItem={csiItem}
          onItemCount={handleTabCount('elec-schedules')}
        />
      </TabsContent>

      {/* Placeholder Tabs */}
      {TABS.filter(t => t.value !== 'elec-general' && t.value !== 'elec-panel' && t.value !== 'elec-equipment' && t.value !== 'elec-maintenance' && t.value !== 'elec-submittals' && t.value !== 'elec-devices' && t.value !== 'elec-schedules').map((tab) => (
        <TabsContent key={tab.value} value={tab.value} data-testid={`content-${tab.value}`}>
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <tab.icon className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">{tab.label}</h3>
            <p className="text-sm text-slate-500">Content for {tab.label} coming soon...</p>
          </div>
        </TabsContent>
      ))}

      <ConfirmDialog {...confirmState} onClose={() => { setConfirmState({ open: false }); pendingTabRef.current = null; }} />
    </Tabs>
  );
};
